import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { 
  ArrowLeft, 
  Loader2,
  AlertCircle,
  CheckCircle,
  Zap,
  Fish,
  Shield,
  AlertCircle as AlertIcon,
  MessageSquare
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'
import api from '../utils/api'

function NewPost() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, loading: authLoading } = useAuth()
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchingCategories, setFetchingCategories] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // 从 URL 获取默认分类 ID
  const defaultCategoryId = searchParams.get('categoryId')
  console.log('URL categoryId:', defaultCategoryId)

  useEffect(() => {
    console.log('[NewPost] useEffect called:', { authLoading, user: !!user })

    // 等待 AuthContext 初始化完成
    if (authLoading) return

    if (!user) {
      console.log('[NewPost] User not logged in, redirecting to login...')
      goToLogin('/login')
      return
    }
    // 传递 defaultCategoryId 给 fetchCategories
    fetchCategories(defaultCategoryId)
  }, [user, authLoading, navigate, defaultCategoryId])

  const fetchCategories = async (targetCategoryId) => {
    console.log('fetchCategories called with:', targetCategoryId)
    try {
      const response = await api.get('/forum/categories')
      console.log('Categories loaded:', response.data.categories)
      if (response.data.success) {
        setCategories(response.data.categories)
        if (response.data.categories.length > 0) {
          // 如果 URL 中有指定分类 ID，使用它；否则使用第一个
          if (targetCategoryId) {
            const matchedCategory = response.data.categories.find(c => c.id === parseInt(targetCategoryId))
            console.log('Matched category:', matchedCategory)
            if (matchedCategory) {
              console.log('Setting selected category to:', matchedCategory.id)
              setSelectedCategory(matchedCategory.id)
            } else {
              console.log('No match found, using first category:', response.data.categories[0].id)
              setSelectedCategory(response.data.categories[0].id)
            }
          } else {
            console.log('No targetCategoryId, using first category:', response.data.categories[0].id)
            setSelectedCategory(response.data.categories[0].id)
          }
        }
      }
    } catch (error) {
      console.error('获取板块失败:', error)
    } finally {
      setFetchingCategories(false)
    }
  }

  const getCategoryIcon = (slug) => {
    switch (slug) {
      case 'skill': return <Zap className="w-4 h-4" />
      case 'shrimp': return <Fish className="w-4 h-4" />
      case 'guide': return <Shield className="w-4 h-4" />
      case 'complaint': return <AlertIcon className="w-4 h-4" />
      default: return <MessageSquare className="w-4 h-4" />
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!selectedCategory || fetchingCategories) {
      setError('请选择板块')
      return
    }
    
    if (!title.trim()) {
      setError('标题不能为空')
      return
    }
    
    if (!content.trim()) {
      setError('内容不能为空')
      return
    }

    setLoading(true)
    setError('')
    
    try {
      // 确保 category_id 是有效的数字
      const categoryId = parseInt(selectedCategory)
      if (isNaN(categoryId) || categoryId <= 0) {
        setError('请选择有效的板块')
        setLoading(false)
        return
      }
      
      const response = await api.post('/forum/posts', {
        category_id: categoryId,
        title,
        content
      })
      
      if (response.data.success) {
        const limitMsg = response.data.postLimitMessage || ''
        setSuccess(`发布成功！${limitMsg}`)
        setTimeout(() => {
          navigate(`/forum/post/${response.data.id}`)
        }, 1500)
      } else {
        // 由于 api.js 拦截器将 message 映射为了 error 属性，所以从这里取值
        setError(response.data.error || response.data.message || '发布失败')
      }
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.message || '发布失败')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="animate-in">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/forum')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-dclaw-600 dark:hover:text-dclaw-400 mb-4 sm:mb-6 text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          返回讨论区
        </button>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 border border-gray-200 dark:border-gray-700">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-5 sm:mb-6">
            发布新帖子
          </h1>
          
          {/* Loading State */}
          {authLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-dclaw-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400 text-sm sm:text-base">验证登录状态...</span>
            </div>
          )}

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">{error}</span>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">{success}</span>
            </div>
          )}

          {!authLoading && (
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                选择板块 *
              </label>
              {fetchingCategories ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  加载中...
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-medium transition-colors min-h-[44px] ${
                        selectedCategory === cat.id
                          ? 'bg-dclaw-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {getCategoryIcon(cat.slug)}
                      <span className="truncate">{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                标题 *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请输入标题（1-100字）"
                maxLength={100}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white transition-all text-sm sm:text-base min-h-[44px]"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                {title.length}/100
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                内容 *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请输入内容（至少1字）"
                rows={6}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white resize-none transition-all text-sm sm:text-base"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                {content.length} 字
              </p>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 gap-4 sm:gap-0">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                发布帖子可获得 10 积分
              </p>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => navigate('/forum')}
                  className="flex-1 sm:flex-none px-5 sm:px-6 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedCategory || !title.trim() || !content.trim()}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      发布中...
                    </>
                  ) : (
                    '发布帖子'
                  )}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewPost
